import { useEffect, useState } from "react";
import { Users, Ticket, CalendarCheck, ChevronDown } from "lucide-react";
import { adminApi } from "@/lib/api";
import { fmtTime } from "@/pages/TicketVerify";

const Bar = ({ got, of }) => (
  <div className="h-1.5 bg-current/20 mt-2 overflow-hidden" style={{ backgroundColor: "rgba(127,127,127,.25)" }}>
    <div className="h-full bg-[var(--trio-primary)] transition-[width] duration-500" style={{ width: of ? `${Math.min(100, (100 * got) / of)}%` : 0 }} />
  </div>
);

const Stat = ({ icon: Icon, label, got, of, sub, testid }) => (
  <div data-testid={testid} className="flex-1 min-w-0 p-3">
    <div className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase opacity-70"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="font-display text-3xl leading-none mt-1">
      {got}<span className="text-base opacity-60"> / {of}</span>
    </div>
    <div className="text-[11px] opacity-70 mt-1">{sub}</div>
    <Bar got={got} of={of} />
  </div>
);

export const DoorSummary = ({ refreshKey = 0, dark = false }) => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => adminApi.get("/staff/door-summary").then(({ data }) => setS(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [refreshKey]);

  if (!s) return null;
  const border = dark ? "border-white/10" : "border-[var(--trio-border)]";

  return (
    <div data-testid="door-summary" className={`border ${border} ${dark ? "bg-white/5 text-white" : "bg-[var(--trio-surface)]"}`}>
      <div className={`flex items-center justify-between px-3 pt-3`}>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[var(--trio-primary)]">
          Tonight at the Door · {s.date}
        </div>
        <div data-testid="door-summary-people-in" className="flex items-center gap-1 font-display text-xl">
          <Users className="w-4 h-4" /> {s.people_in} in
        </div>
      </div>
      {s.events.length > 0 && <div className="px-3 text-xs opacity-70 truncate">{s.events.join(" · ")}</div>}
      <div className={`flex divide-x ${dark ? "divide-white/10" : "divide-[var(--trio-border)]"}`}>
        <Stat icon={Ticket} label="Tickets" got={s.tickets.in_people} of={s.tickets.people} sub={`${s.tickets.in_orders} of ${s.tickets.orders} orders`} testid="door-summary-tickets" />
        <Stat icon={CalendarCheck} label="RSVPs" got={s.rsvps.in_guests} of={s.rsvps.guests} sub={`${s.rsvps.in_parties} of ${s.rsvps.parties} parties`} testid="door-summary-rsvps" />
      </div>
      {s.recent.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="door-summary-toggle" className={`w-full flex items-center justify-between px-3 py-2 border-t ${border} text-[11px] uppercase tracking-widest`}>
            Recent check-ins <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ul data-testid="door-summary-recent" className="px-3 pb-3 space-y-1 text-sm max-h-56 overflow-y-auto">
              {s.recent.map((r, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="truncate inline-flex items-center gap-1.5">{r.kind === "ticket" ? <Ticket className="w-3.5 h-3.5 shrink-0" /> : <CalendarCheck className="w-3.5 h-3.5 shrink-0" />} {r.name} · {r.count}</span>
                  <span className="opacity-60 text-xs whitespace-nowrap">{fmtTime(r.at).split(", ").pop()}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
